import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import FlexLib from '@flex/lib';
import {
  Container,
  Checkbox,
  Grid,
  FormControlLabel,
  Autocomplete,
  TextField,
  Backdrop,
  CircularProgress,
  Button,
  Typography,
  Stack,
} from '@mui/material';
import { FlexAlerts, AlertSeverityTypes } from '../../../components/Message/Alerts/Alerts';
import { DataGetApiV2 } from '../../../helpers/PostDataApi';
import { getBinLookup } from '../../../services/binLookupService';
import transactionsService from '../../../services/transactionsService';
import { setTsepAuth } from '../../../actions/LocationAction';
import config from '../../../constants/config';
import commonStrings from '../../../constants/commonStrings';
import { getLocationPaymentGateway } from '../../../helpers/commonFunction';
import autoFillMultipassFields from '../../../helpers/functions/autoFillCardFields';
import { checkEmail } from '../../../helpers/util';
import { changeFocus } from '../../../helpers/changeFocusCard';
import AmountTextField from '../../../components/FeeDisplay/AmountTextField';
import TsepCardV2 from '../../../components/CardForm/TsepCard';
import PaytraceCard from '../../../components/CardForm/PaytraceCard';
import { BackButton } from '../../../components/Buttons';

const { formatCurrency, sumList, subtractAmounts, formatCurrencyInput } = FlexLib.utils.feeService;

const RefundTab = () => {
  const numberOfTransactionsInList = 100;
  const [isLoading, setIsLoading] = useState(false);
  const [isRefundSuccessful, setIsRefundSuccessful] = useState(false);
  const [isManualRefund, setIsManualRefund] = useState(false);
  const [isManualRefundCheckboxDisabled, setIsManualRefundCheckboxDisabled] = useState(true);
  const [paymentGateway, setPaymentGateway] = useState('');
  const [messages, setMessages] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionsList, setTransactionsList] = useState([]);
  const [refundAmount, setRefundAmount] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [binCodesResponse, setBinCodesResponse] = useState({ bin: '', card: '', type: '' });
  const [tsepResponse, setTsepResponse] = useState({});
  const dispatch = useDispatch();

  // Needed for card forms
  const [card, setCard] = useState({});
  const [cardErrors, setCardErrors] = useState({});
  const cardRef = useRef();
  const cscCardRef = useRef();
  const expiryDateRef = useRef();

  useEffect(() => {
    getTransactionsList();
    const gateway = getLocationPaymentGateway();
    setPaymentGateway(gateway);

    getRefundPermissions();
  }, []);

  useEffect(() => {
    if (paymentGateway === commonStrings.multipass) {
      document.addEventListener('tsepEventUpdate', handleTsepEventUpdated);
    } else if (paymentGateway === commonStrings.paytrace) {
      let locationDetails = localStorage.getItem('locationPaydetails');
      locationDetails = JSON.parse(locationDetails) || {};
      if (locationDetails?.paytraceKey) {
        const localUrl = config.BASEURL + 'getPemFile?fileName=';
        window.paytrace.setKeyAjax(localUrl + locationDetails?.paytraceKey);
      }
    }

    return () => {
      document.removeEventListener('tsepEventUpdate', handleTsepEventUpdated);
    };
  }, [paymentGateway]);

  useEffect(() => {
    if (paymentGateway === commonStrings.multipass && selectedTransaction?.isACH) {
      setMessages([{ severity: AlertSeverityTypes.info, message: 'ACH transactions cannot be partially refunded.' }]);
    } else {
      setMessages([]);
    }
  }, [selectedTransaction]);

  useEffect(() => {
    if (messages?.length > 0) {
      window.scrollTo(0, 0);
    }
  }, [messages]);

  const getRefundPermissions = () => {
    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};

    if (user?.permissions?.refundsManual) {
      setIsManualRefundCheckboxDisabled(false);
    } else {
      setIsManualRefund(false);
      setIsManualRefundCheckboxDisabled(true);
    }
  };

  const getTransactionsList = () => {
    let locationDetails = localStorage.getItem('locationPaydetails');
    locationDetails = JSON.parse(locationDetails) || {};
    if (!locationDetails?.id || !locationDetails?.company) {
      setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not retrieve transactions for refund.' }]);
      return;
    }
    setIsLoading(true);
    DataGetApiV2(config.BASEURL + 'getTransactionsDataForRefund', {
      companyId: locationDetails.company,
      locationId: locationDetails.id,
    })
      .then((response) => {
        if (response.data?.response) {
          formatTransactionsList(response.data.response);
        }
      })
      .catch((_error) => {
        setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not retrieve transactions for refund.' }]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const formatTransactionsList = (list) => {
    const filteredTransactions = list
      .filter((transaction) => {
        if (transaction.status_code === 'P' || transaction.status_code === 'V') {
          return false;
        }
        if (transaction.REFUNDED?.length > 0) {
          const validRefunds = transaction.REFUNDED.filter((x) => x.success);
          const refundedAmount = sumList(validRefunds, 'amt');
          return refundedAmount < transaction.AMOUNT; // is it already all refunded
        }
        return true;
      })
      .slice(0, numberOfTransactionsInList);

    // transaction number - date - status - customer name - amount
    const formattedTransactions = filteredTransactions.map((transaction) => {
      const date = `${new Date(transaction.DATETIME).toLocaleDateString()} ${new Date(
        transaction.DATETIME
      ).toLocaleTimeString()}`;
      let amount = transaction.AMOUNT?.toString() ?? '';
      if (transaction.REFUNDED?.length > 0) {
        const validRefunds = transaction.REFUNDED.filter((x) => x.success);
        const refundedAmount = sumList(validRefunds, 'amt');
        amount = subtractAmounts(amount, refundedAmount);
      }

      let status;
      switch (transaction.status_code) {
        case 'P':
          status = 'Unsettled';
          break;
        case 'V':
          status = 'Voided';
          break;
        default:
          status = 'Settled';
      }

      const customerName = `${transaction.CUSTOMERDATA?.firstName ?? ''}${
        transaction.CUSTOMERDATA?.firstName && transaction.CUSTOMERDATA?.lastName ? ' ' : ''
      }${transaction.CUSTOMERDATA?.lastName ?? ''}`;

      const formatted = {
        label: `${transaction.TXNID} - ${date} - ${status} - ${customerName} - ${formatCurrency(amount)}`,
        value: transaction._id,
        amount,
        customerFirstName: transaction.CUSTOMERDATA?.firstName,
        customerLastName: transaction.CUSTOMERDATA?.lastName,
        email: transaction.email ?? transaction.CUSTOMERDATA?.email,
        accountNumber: transaction.MASKCARDNUMBER,
        isACH: commonStrings.ach === transaction.creditOrDebit,
      };
      return formatted;
    });

    setTransactionsList(formattedTransactions);
  };

  const handleToggleManualRefund = (_event, checked) => {
    setCard({});
    setSelectedTransaction(null);
    setRefundAmount('');
    setIsManualRefund(checked);
  };

  const handleSelectTransaction = (_event, value) => {
    let transaction = null;
    if (value?.value) {
      transaction = transactionsList.find((x) => x.value === value?.value);
    }
    setSelectedTransaction(transaction);
    setRefundAmount(formatCurrencyInput(transaction?.amount) ?? '');
  };

  const getPaymentMethod = () => {
    const paymentGateway = getLocationPaymentGateway();
    const paymentMethod = {
      encryptedCard: '',
      encryptedCSC: '',
      expirationDate: '',
      accountName: '',
      paymentType: binCodesResponse.type?.toLowerCase(),
      cardNetwork: binCodesResponse.card,
      binCodesResponse: binCodesResponse,
    };

    if (paymentGateway === commonStrings.multipass) {
      paymentMethod.encryptedCard = tsepResponse?.tsepToken ? tsepResponse?.tsepToken : null;
      paymentMethod.encryptedCSC = tsepResponse?.cvv2 ? tsepResponse?.cvv2 : null;
      paymentMethod.expirationDate = tsepResponse?.expirationDate;
    } else if (paymentGateway === commonStrings.paytrace) {
      window.paytrace.submitEncrypted('#DemoFormTerminal');
      paymentMethod.encryptedCard = localStorage.getItem('0');
      paymentMethod.encryptedCSC = localStorage.getItem('1');
      paymentMethod.expirationDate = card?.cardExpiryDate;
      localStorage.removeItem('0');
      localStorage.removeItem('1');
    }

    return paymentMethod;
  };

  const handleRunRefund = () => {
    if (!refundAmount) {
      setMessages([{ severity: AlertSeverityTypes.error, message: 'Requires amount.' }]);
      return;
    }

    if (isManualRefund) {
      if (customerEmail && !checkEmail(customerEmail)) {
        setMessages([{ severity: AlertSeverityTypes.error, message: 'Email is invalid.' }]);
        return;
      }

      setIsLoading(true);

      const requestBody = {
        refundAmount,
        customerEmail,
        source: 'Virtual Terminal',
        paymentMethod: getPaymentMethod(),
      };
      transactionsService
        .manualRefund(requestBody)
        .then((response) => {
          setMessages([
            {
              severity: AlertSeverityTypes.success,
              message: response?.data?.message ?? 'Refund processed successfully.',
            },
          ]);
          setIsRefundSuccessful(true);
        })
        .catch((error) => {
          setMessages([
            { severity: AlertSeverityTypes.error, message: error.response?.data?.message ?? 'Refund failed.' },
          ]);
        })
        .finally(() => setIsLoading(false));
    } else {
      const refundTransactionId = selectedTransaction.value;

      if (!refundTransactionId) {
        setMessages([{ severity: AlertSeverityTypes.error, message: 'Please select a transaction.' }]);
        return;
      }

      setIsLoading(true);

      const requestBody = {
        refundAmount,
        source: 'Virtual Terminal',
      };
      transactionsService
        .refundTransaction(refundTransactionId, requestBody)
        .then((response) => {
          setMessages([
            {
              severity: AlertSeverityTypes.success,
              message: response?.data?.message ?? 'Refund processed successfully.',
            },
          ]);
          setIsRefundSuccessful(true);
        })
        .catch((error) => {
          setMessages([
            { severity: AlertSeverityTypes.error, message: error.response?.data?.message ?? 'Refund failed.' },
          ]);
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleChangePaytraceCard = (event) => {
    let { id, value } = event.target;

    setCardErrors({ cardNumber: false, cardExpiryDate: false, cardCsc: false });

    // Validate values
    if (id === 'cardNumber') {
      const cleanedNumber = value?.replace(/[_ ]/g, '');
      if (cleanedNumber.length >= 6) {
        getBinCodesResponse(cleanedNumber.slice(0, 6));
      }
    }
    if (value.length === 16 && value[value.length - 1] === '_' && id === 'cardNumber' && value[0] === '3') {
      value = value.slice(0, -1);
    }
    if (id === 'cardExpiryDate') {
      if (!/^(0[1-9]|1[0-2])([/-])?(\d{4})$/gm.test(value)) {
        setCardErrors((prevState) => ({ ...prevState, cardExpiryDate: true }));
      } else {
        setCard((prevState) => ({ ...prevState, cardExpiryDate: value }));
      }
    }
    changeFocus(id, value, expiryDateRef.current, cscCardRef.current);
  };

  const getBinCodesResponse = async (cardBin) => {
    if (cardBin?.length >= 6) {
      getBinLookup(cardBin)
        .then((response) => {
          setBinCodesResponse(response?.binCodesResponse);
        })
        .catch((error) => {
          setMessages([
            { severity: AlertSeverityTypes.error, message: 'Could not complete bin lookup. Please contact support.' },
          ]);
          console.log(error);
        });
    }
  };

  const handleTsepEventUpdated = (event) => {
    const eventTsep = event.detail;
    if (eventTsep.eventType === 'FieldValidationErrorEvent') {
      setMessages([{ severity: AlertSeverityTypes.error, message: eventTsep?.event?.message }]);
    } else if (eventTsep.eventType === 'TokenEvent' && eventTsep.event.status === 'PASS') {
      setTsepResponse({ ...eventTsep.event });
    } else if (eventTsep.eventType === 'TokenEvent' && eventTsep.event.status === 'FAILURE') {
      setMessages([{ severity: AlertSeverityTypes.error, message: eventTsep?.event?.message }]);
    } else if (eventTsep.eventType === 'FocusOutEvent' && eventTsep.event.fieldName === 'tsep-cardNum') {
      const cardNumberElement = document.getElementById('tsep-cardNum');
      if (cardNumberElement) {
        const cardNumberValue = cardNumberElement?.value.replace(/\s/g, '');
        getBinCodesResponse(cardNumberValue);
      }
    } else if (
      eventTsep.eventType === 'ErrorEvent' &&
      eventTsep.event.status === 'FAIL' &&
      eventTsep.event.responseCode === 'TSEPERR911'
    ) {
      dispatch(setTsepAuth(false));
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Could not connect to Multipass. Please reload page to try again or contact support.',
        },
      ]);
    }
  };

  const getCardDisplayData = () => {
    if (isManualRefund) {
      return { showCardInput: true, showSaveCheckbox: false };
    } else {
      return { showCardInput: false, headerText: 'No card data needed', showSaveCheckbox: false };
    }
  };

  return (
    <Container component="section" maxWidth={false}>
      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress style={{ color: 'white' }} />
      </Backdrop>
      {isRefundSuccessful && <Redirect to="/Transactions" />}
      {paymentGateway === 'multipass' && autoFillMultipassFields()}
      <Grid container rowSpacing={2} columnSpacing={3}>
        {messages.length > 0 && (
          <Grid item xs={12} md={12}>
            <FlexAlerts messages={messages} />
          </Grid>
        )}
        <Grid item xs={12} lg={4} id="payment-details-section">
          <form id="DemoFormTerminal">
            <Typography component="h2" gutterBottom>
              Card Details
            </Typography>
            {paymentGateway === commonStrings.multipass && (
              <TsepCardV2 isRefund displayCardDetails={getCardDisplayData()} />
            )}
            {paymentGateway === commonStrings.paytrace && (
              <PaytraceCard
                card={card}
                cardErrors={cardErrors}
                cardRef={cardRef}
                expiryDateRef={expiryDateRef}
                cscCardRef={cscCardRef}
                handleChangeCard={handleChangePaytraceCard}
                displayCardDetails={getCardDisplayData()}
              />
            )}
          </form>
        </Grid>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  value={isManualRefund}
                  disabled={isManualRefundCheckboxDisabled}
                  onChange={handleToggleManualRefund}
                />
              }
              label="Manual Refund"
            />

            {!isManualRefund && (
              <>
                <Autocomplete
                  id="transactions-list"
                  fullWidth
                  options={transactionsList}
                  value={selectedTransaction}
                  renderInput={(params) => <TextField {...params} label="Transaction Number" />}
                  isOptionEqualToValue={(option, value) => !value.value || option.value === value.value}
                  onChange={handleSelectTransaction}
                />
                <TextField
                  id="first-name"
                  fullWidth
                  disabled
                  label="First Name"
                  value={selectedTransaction?.customerFirstName ?? ''}
                />
                <TextField
                  id="last-name"
                  fullWidth
                  disabled
                  label="Last Name"
                  value={selectedTransaction?.customerLastName ?? ''}
                />
                <TextField id="email" fullWidth disabled label="Email" value={selectedTransaction?.email ?? ''} />

                <TextField
                  id="account-number"
                  fullWidth
                  disabled
                  label="Account Number"
                  value={selectedTransaction?.accountNumber ?? ''}
                />
              </>
            )}
            {isManualRefund && (
              <TextField
                id="customer-email"
                fullWidth
                label="Customer Email"
                onChange={(event) => {
                  setCustomerEmail(event?.target?.value);
                }}
                value={customerEmail}
              />
            )}
            <AmountTextField
              id="refund-amount"
              label={isManualRefund ? 'Amount' : 'Total'}
              fullWidth
              amount={refundAmount}
              setAmount={setRefundAmount}
              disabled={paymentGateway === commonStrings.multipass && selectedTransaction?.isACH}
            />
            <Grid container rowSpacing={2} columnSpacing={2} justifyContent={'flex-end'}>
              <Grid item>
                <Button
                  id="run-refund"
                  // Disables one time card payment submission unless tsep response status is passing.
                  disabled={
                    isManualRefund && paymentGateway === commonStrings.multipass && tsepResponse?.status !== 'PASS'
                  }
                  variant="contained"
                  onClick={handleRunRefund}
                >
                  Run Refund
                </Button>
              </Grid>
              <Grid item>
                <BackButton />
              </Grid>
            </Grid>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RefundTab;
